import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { ArrowRight, Check, Star, ShieldCheck, FileText, BookOpen, CalendarClock, Loader2, BarChart3 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1558642084-fd07fae5282e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxzcGFpbiUyMGFyY2hpdGVjdHVyZSUyMHRyYXZlbHxlbnwwfHx8fDE3ODMzNzc4ODR8MA&ixlib=rb-4.1.0&q=85";

const testimonials = [
  { name: "Elena R.", role: "Moved to Lisbon → Barcelona", quote: "The Spain guide answered every question I had about the digital nomad visa. Saved me weeks of scattered research." },
  { name: "Marcus T.", role: "Remote engineer, Berlin", quote: "Clear, structured, and genuinely useful. The first-90-days checklist alone was worth far more than the price." },
  { name: "Priya & Sam", role: "Family relocating to Italy", quote: "We bought the bundle to compare countries. The cost-of-living breakdowns made our decision so much easier." },
];

const CountryCard = ({ g, i }) => (
  <Link
    to={`/guides/${g.slug}`}
    data-testid={`guide-card-${g.slug}`}
    className="group relative overflow-hidden border border-stone-200 bg-white fade-up"
    style={{ animationDelay: `${i * 80}ms` }}
  >
    <div className="relative aspect-[3/4] overflow-hidden">
      <img src={g.cover_url} alt={g.country} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute top-4 left-4">
        <span className="text-3xl drop-shadow-md">{g.flag}</span>
      </div>
      {g.entitled && (
        <Badge className="absolute top-4 right-4 bg-[#166534] text-white rounded-none border-0"><Check className="w-3 h-3 mr-1" />Owned</Badge>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
        <h3 className="font-serif text-3xl font-medium">{g.country}</h3>
        <p className="text-sm text-stone-200 mt-1 line-clamp-2">{g.tagline}</p>
      </div>
    </div>
    <div className="flex items-center justify-between px-5 py-4">
      <span className="text-sm text-stone-500">{g.entitled ? "In your library" : `from $${g.price.toFixed(2)}`}</span>
      <span className="flex items-center gap-1 text-sm font-medium text-[#9A3412] group-hover:gap-2 transition-all">
        {g.entitled ? "Read" : "View guide"} <ArrowRight className="w-4 h-4" />
      </span>
    </div>
  </Link>
);

const Storefront = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["guides"],
    queryFn: async () => (await api.get("/guides")).data,
  });

  return (
    <div className="bg-[#F9F9F7]">
      <Seo
        title="UMoooV — Relocation Guides for Moving Abroad (9 Countries)"
        description="Expert relocation guides for moving abroad to Italy, France, Spain, Germany, Sweden, the UAE, Portugal, the USA and Canada. Visas, taxes, housing, healthcare, banking & cost of living — read in-app or download the PDF. Move abroad with clarity, not chaos."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "UMoooV",
          "url": "https://umooov.info",
          "description": "Professionally structured relocation guides for 9 top destinations.",
        }}
      />
      {/* Hero */}
      <section className="relative h-[78vh] min-h-[560px] w-full overflow-hidden">
        <img src={HERO} alt="Relocate abroad" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
          <div className="max-w-2xl fade-up">
            <p className="text-white/80 text-sm uppercase tracking-[0.2em] mb-5">Relocation guides that actually help</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-medium text-white leading-[1.05]">
              Move abroad with clarity, not chaos.
            </h1>
            <p className="mt-6 text-lg text-stone-100/90 max-w-xl leading-relaxed">
              Professionally structured guides for nine of the world's most sought-after destinations. Visas, taxes, housing, healthcare — everything you need, in one place.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Button asChild className="bg-[#9A3412] hover:bg-[#7C2D12] text-white rounded-none px-8 py-6 text-base">
                <a href="#storefront" data-testid="hero-browse-btn">Browse guides <ArrowRight className="w-4 h-4 ml-2" /></a>
              </Button>
              <Button asChild variant="outline" className="rounded-none px-8 py-6 text-base bg-white/10 border-white/40 text-white hover:bg-white/20">
                <a href="#bundle" data-testid="hero-bundle-btn">See the bundle</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-b border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { icon: BookOpen, label: "10-section framework" },
            { icon: FileText, label: "Print-ready PDF included" },
            { icon: CalendarClock, label: '"Last updated" dates' },
            { icon: ShieldCheck, label: "Read on any device" },
          ].map((f, i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <f.icon className="w-6 h-6 text-[#9A3412]" />
              <span className="text-sm text-stone-600">{f.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Storefront grid */}
      <section id="storefront" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="mb-12 max-w-2xl">
          <p className="text-sm uppercase tracking-widest text-[#9A3412] mb-3">The collection</p>
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-stone-900">Choose your destination</h2>
          <p className="mt-4 text-stone-600">Each guide is a complete relocation playbook — from your first visa question to your first ninety days on the ground.</p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#9A3412]" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {data?.guides?.map((g, i) => <CountryCard key={g.slug} g={g} i={i} />)}
          </div>
        )}
      </section>

      {/* Compare CTA */}
      <section className="border-y border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 shrink-0 bg-[#9A3412]/10 flex items-center justify-center">
              <BarChart3 className="w-7 h-7 text-[#9A3412]" />
            </div>
            <div>
              <h3 className="font-serif text-2xl md:text-3xl text-stone-900">Can't decide where to move?</h3>
              <p className="text-stone-600 mt-1">Compare the monthly cost of living across all 9 destinations, side by side.</p>
            </div>
          </div>
          <Button asChild className="bg-[#9A3412] hover:bg-[#7C2D12] text-white rounded-none px-8 py-6 text-base shrink-0">
            <Link to="/compare" data-testid="compare-cta-btn">Compare cost of living <ArrowRight className="w-4 h-4 ml-2" /></Link>
          </Button>
        </div>
      </section>

      {/* Bundle */}
      {data?.bundle && (
        <section id="bundle" className="bg-stone-900 text-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative aspect-[4/3] overflow-hidden border border-stone-700">
              <img src={data.bundle.cover_url} alt="Bundle" className="w-full h-full object-cover" />
            </div>
            <div>
              <Badge className="bg-[#C2410C] text-white rounded-none border-0 mb-5">Best value</Badge>
              <h2 className="font-serif text-4xl md:text-5xl font-medium">{data.bundle.subtitle}</h2>
              <p className="mt-4 text-stone-300 leading-relaxed max-w-lg">{data.bundle.description}</p>
              <div className="mt-6 flex items-baseline gap-3">
                <span className="font-serif text-5xl">${data.bundle.price.toFixed(2)}</span>
                <span className="text-stone-400 line-through">$44.91</span>
                <span className="text-[#22C55E] text-sm">Save 33%</span>
              </div>
              <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-lg">
                {["Italy", "France", "Spain", "Germany", "Sweden", "UAE", "Portugal", "United States", "Canada"].map((c) => (
                  <li key={c} className="flex items-center gap-2 text-sm text-stone-200"><Check className="w-4 h-4 text-[#22C55E]" />{c}</li>
                ))}
              </ul>
              <Button asChild className="mt-8 bg-white text-stone-900 hover:bg-stone-200 rounded-none px-8 py-6 text-base">
                <Link to="/guides/all-access-bundle" data-testid="bundle-cta-btn">Get the bundle <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="mb-12 text-center">
          <p className="text-sm uppercase tracking-widest text-[#9A3412] mb-3">Loved by movers</p>
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-stone-900">People who moved with UMoooV</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((t, i) => (
            <div key={i} data-testid={`testimonial-${i}`} className="border border-stone-200 bg-white p-8">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#9A3412] text-[#9A3412]" />)}
              </div>
              <p className="font-reader text-stone-700 leading-relaxed italic">"{t.quote}"</p>
              <div className="mt-6">
                <p className="font-medium text-stone-900">{t.name}</p>
                <p className="text-sm text-stone-500">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Storefront;
